import React, { useState, useEffect, useCallback } from 'react';
import {
  FaRobot, FaSave, FaKey, FaInfoCircle,
  FaChartBar, FaTachometerAlt, FaChartLine, FaUsers, FaCommentDots,
  FaUserCheck, FaCalendarAlt, FaCrown, FaShieldAlt, FaCheckCircle,
  FaBolt, FaLock
} from 'react-icons/fa';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import { apiHelper } from '../../utils/api';
import { showToast } from '../../utils/toast';
import CustomSelect from '../../components/CustomSelect/CustomSelect';
import './Admin.scss';

const COLORS = { primary: '#6366F1', success: '#10B981', warning: '#F59E0B', info: '#3B82F6' };

const AiAssistantSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEnabled, setIsEnabled] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [hasApiKey, setHasApiKey] = useState(false);
  const [keyChanged, setKeyChanged] = useState(false);
  const [dailyRequestLimit, setDailyRequestLimit] = useState(1000);
  const [rateLimitPerMinute, setRateLimitPerMinute] = useState(15);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsPeriod, setStatsPeriod] = useState('all');
  const [statsCustomFrom, setStatsCustomFrom] = useState('');
  const [statsCustomTo, setStatsCustomTo] = useState('');

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const data = await apiHelper.get('/admin/ai-settings');
      setIsEnabled(data.isEnabled);
      setHasApiKey(data.hasApiKey);
      setDailyRequestLimit(data.dailyRequestLimit ?? 1000);
      setRateLimitPerMinute(data.rateLimitPerMinute ?? 15);
      setApiKey('');
      setKeyChanged(false);
    } catch (err) {
      showToast('error', 'Failed to load AI settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      let url = `/admin/ai/stats?period=${statsPeriod}`;
      if (statsPeriod === 'custom' && statsCustomFrom && statsCustomTo) {
        url += `&startDate=${statsCustomFrom}&endDate=${statsCustomTo}`;
      }
      const data = await apiHelper.get(url);
      setStats(data);
    } catch (err) {
      // silently fail - stats is non-critical
    } finally {
      setStatsLoading(false);
    }
  }, [statsPeriod, statsCustomFrom, statsCustomTo]);

  useEffect(() => { fetchSettings(); }, []);
  useEffect(() => { if (!loading) fetchStats(); }, [loading, fetchStats, statsCustomFrom, statsCustomTo]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        isEnabled,
        dailyRequestLimit: Math.max(1, parseInt(dailyRequestLimit, 10) || 1000),
        rateLimitPerMinute: Math.max(1, parseInt(rateLimitPerMinute, 10) || 15),
      };
      if (keyChanged && apiKey.trim()) {
        payload.apiKey = apiKey.trim();
      }
      await apiHelper.put('/admin/ai-settings', payload);
      showToast('success', 'AI settings saved');
      setHasApiKey(keyChanged ? !!apiKey.trim() : hasApiKey);
      setKeyChanged(false);
      setApiKey('');
    } catch (err) {
      showToast('error', 'Failed to save AI settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page-loading">
        <div className="admin-spinner" />
        <p>Loading AI settings...</p>
      </div>
    );
  }

  return (
    <div className="admin-page site-settings-page ai-assistant-page">
      <div className="ai-hero">
        <div className="ai-hero__icon">
          <FaRobot />
        </div>
        <div className="ai-hero__text">
          <div className="ai-hero__badge-row">
            <span className="ai-hero__eyebrow">Admin • AI Controls</span>
            <span className={`ai-status-pill ${isEnabled ? 'ai-status-pill--on' : 'ai-status-pill--off'}`}>
              <span className="ai-status-pill__dot" />
              {isEnabled ? 'Live for all users' : 'Paused for all users'}
            </span>
          </div>
          <h1 className="admin-page-title ai-hero__title">AI Assistant Settings</h1>
          <p className="admin-page-subtitle ai-hero__sub">
            Control the AI assistant for all users. Update the API key or disable the assistant entirely.
          </p>
        </div>
      </div>

      <div className="ai-settings-wrap">
        <div className="settings-card ai-settings-card">
          <div className="ai-settings-card__glow" aria-hidden="true" />
          <div className="settings-card-header ai-settings-card__header">
            <div className="ai-settings-card__title-group">
              <div className="settings-card-icon ai-settings-card__icon">
                <FaRobot />
              </div>
              <div className="ai-settings-card__titles">
                <h2 className="settings-card-title">AI Assistant Status</h2>
                <p className="settings-card-desc">
                  Enable or disable the AI assistant across the entire platform.
                </p>
              </div>
            </div>
            <button
              className={`ai-switch ${isEnabled ? 'ai-switch--on' : ''}`}
              onClick={() => setIsEnabled(!isEnabled)}
              aria-label="Toggle AI assistant"
              aria-pressed={isEnabled}
              title={isEnabled ? 'AI is enabled for all users' : 'AI is disabled for all users'}
              type="button"
            >
              <span className="ai-switch__track">
                <span className="ai-switch__thumb" />
              </span>
              <span className="ai-switch__label">{isEnabled ? 'Enabled' : 'Disabled'}</span>
            </button>
          </div>

          {!isEnabled && (
            <div className="ai-paused-banner" role="status">
              <FaInfoCircle className="ai-paused-banner__icon" />
              <p>AI is currently <strong>disabled</strong>. Users won&apos;t see the assistant until you re-enable it and save.</p>
            </div>
          )}

          <div className={`settings-fields ai-settings-card__body ${!isEnabled ? 'fields-disabled' : ''}`}>
            <div className="field-group ai-field">
              <div className="ai-field__label-row">
                <label className="field-label ai-field__label" htmlFor="ai-gemini-key">
                  <span className="ai-field__label-icon"><FaKey /></span>
                  Gemini API Key
                </label>
                <span className={`ai-key-badge ${hasApiKey || keyChanged ? 'ai-key-badge--set' : 'ai-key-badge--unset'}`}>
                  {hasApiKey && !keyChanged ? (
                    <><FaCheckCircle /> Configured</>
                  ) : keyChanged && apiKey.trim() ? (
                    <><FaBolt /> New key ready</>
                  ) : hasApiKey ? (
                    <><FaCheckCircle /> Configured</>
                  ) : (
                    <><FaLock /> Not set</>
                  )}
                </span>
              </div>
              <div className="input-with-info">
                <div className="ai-input-wrap">
                  <FaKey className="ai-input-wrap__icon" />
                  <input
                    id="ai-gemini-key"
                    type="password"
                    className="field-input ai-input"
                    placeholder={hasApiKey ? 'Leave blank to keep existing key' : 'Enter your Gemini API key'}
                    value={apiKey}
                    onChange={(e) => {
                      setApiKey(e.target.value);
                      setKeyChanged(true);
                    }}
                    disabled={!isEnabled}
                    autoComplete="new-password"
                  />
                </div>
                {hasApiKey && !keyChanged ? (
                  <span className="field-hint ai-hint">
                    <FaInfoCircle />
                    An API key is already configured. Enter a new key only to replace it.
                  </span>
                ) : (
                  <span className="field-hint ai-hint">
                    <FaInfoCircle />
                    Paste your Gemini key from Google AI Studio. It applies instantly after save.
                  </span>
                )}
              </div>
            </div>

            <div className="ai-limits-grid">
              <div className="field-group ai-field ai-limit-card">
                <label className="field-label ai-field__label" htmlFor="ai-daily-limit">
                  <span className="ai-field__label-icon ai-field__label-icon--green"><FaChartBar /></span>
                  Daily limit
                </label>
                <div className="ai-input-wrap ai-input-wrap--with-suffix">
                  <input
                    id="ai-daily-limit"
                    type="number"
                    className="field-input ai-input"
                    placeholder="e.g. 1000"
                    value={dailyRequestLimit}
                    onChange={(e) => setDailyRequestLimit(e.target.value)}
                    disabled={!isEnabled}
                    min="1"
                    inputMode="numeric"
                  />
                  <span className="ai-input-suffix">/ day</span>
                </div>
                <span className="field-hint ai-hint">
                  <FaInfoCircle />
                  Max AI queries per user per day. Resets at 1AM.
                </span>
              </div>

              <div className="field-group ai-field ai-limit-card">
                <label className="field-label ai-field__label" htmlFor="ai-rate-limit">
                  <span className="ai-field__label-icon ai-field__label-icon--amber"><FaTachometerAlt /></span>
                  Rate limit
                </label>
                <div className="ai-input-wrap ai-input-wrap--with-suffix">
                  <input
                    id="ai-rate-limit"
                    type="number"
                    className="field-input ai-input"
                    placeholder="e.g. 15"
                    value={rateLimitPerMinute}
                    onChange={(e) => setRateLimitPerMinute(e.target.value)}
                    disabled={!isEnabled}
                    min="1"
                    inputMode="numeric"
                  />
                  <span className="ai-input-suffix">/ min</span>
                </div>
                <span className="field-hint ai-hint">
                  <FaInfoCircle />
                  Max queries per user per minute to prevent spam.
                </span>
              </div>
            </div>

            <div className="field-group">
              <div className="info-box ai-secure-note">
                <span className="ai-secure-note__icon"><FaShieldAlt /></span>
                <div className="ai-secure-note__text">
                  <strong>Encrypted &amp; instant.</strong>
                  <span> Key is stored encrypted in the database and overrides the server env key. Changes apply immediately — no restart needed.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="settings-save-bar ai-save-bar">
        <p className="settings-save-hint ai-save-bar__hint"><span className="ai-save-bar__dot" />Remember to save after making changes.</p>
        <button className="btn btn-primary ai-save-bar__btn" onClick={handleSave} disabled={saving} type="button">
          <FaSave /> {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* ── AI Usage Analytics ── */}
      <div style={{ marginTop: 48 }}>
        <div className="admin-page-header">
          <div className="admin-page-header-left">
            <div>
              <h1 className="admin-page-title" style={{ fontSize: 22 }}>
                <FaChartLine style={{ marginRight: 10, color: '#6366f1' }} />
                AI Usage Analytics
              </h1>
              <p className="admin-page-subtitle">
                See how users are interacting with the AI assistant.
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <FaCalendarAlt style={{ color: '#9ca3af' }} />
            <CustomSelect
              value={statsPeriod}
              onChange={(val) => { setStatsPeriod(val); if (val !== 'custom') { setStatsCustomFrom(''); setStatsCustomTo(''); } }}
              className="admin-select"
              options={[
                { value: 'all', label: 'All Time' },
                { value: '7', label: 'Last 7 days' },
                { value: '30', label: 'Last 30 days' },
                { value: '90', label: 'Last 90 days' },
                { value: '365', label: 'Last year' },
                { value: 'custom', label: 'Custom Range' },
              ]}
            />
            {statsPeriod === 'custom' && (
              <div className="admin-date-range">
                <input
                  type="date"
                  value={statsCustomFrom}
                  onChange={(e) => setStatsCustomFrom(e.target.value)}
                  max={statsCustomTo || new Date().toISOString().split('T')[0]}
                  className="admin-date-input"
                />
                <span className="admin-date-separator">to</span>
                <input
                  type="date"
                  value={statsCustomTo}
                  onChange={(e) => setStatsCustomTo(e.target.value)}
                  min={statsCustomFrom}
                  max={new Date().toISOString().split('T')[0]}
                  className="admin-date-input"
                />
              </div>
            )}
          </div>
        </div>

        {statsLoading && !stats ? (
          <div className="admin-page-loading" style={{ padding: '40px 0' }}>
            <div className="admin-spinner" />
            <p>Loading stats...</p>
          </div>
        ) : stats ? (
          <>
            {/* Today's Metrics */}
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <FaCalendarAlt style={{ color: '#6366f1', fontSize: 13 }} />
                Today
              </h3>
              <div className="admin-analytics-metrics">
                <div className="admin-metric-card">
                  <div className="admin-metric-card__icon admin-metric-card__icon--primary" style={{ background: 'rgba(99,102,241,0.1)' }}>
                    <FaCommentDots />
                  </div>
                  <div className="admin-metric-card__content">
                    <span className="admin-metric-card__value">{stats.today.totalQueries.toLocaleString()}</span>
                    <span className="admin-metric-card__label">Total Queries</span>
                    <span className="admin-metric-card__sub">Today</span>
                  </div>
                </div>
                <div className="admin-metric-card">
                  <div className="admin-metric-card__icon admin-metric-card__icon--success" style={{ background: 'rgba(16,185,129,0.1)' }}>
                    <FaUserCheck />
                  </div>
                  <div className="admin-metric-card__content">
                    <span className="admin-metric-card__value">{stats.today.activeUsers}</span>
                    <span className="admin-metric-card__label">Active Users</span>
                    <span className="admin-metric-card__sub">Made at least 1 query</span>
                  </div>
                </div>
                <div className="admin-metric-card">
                  <div className="admin-metric-card__icon admin-metric-card__icon--warning" style={{ background: 'rgba(245,158,11,0.1)' }}>
                    <FaChartBar />
                  </div>
                  <div className="admin-metric-card__content">
                    <span className="admin-metric-card__value">{stats.today.avgPerUser}</span>
                    <span className="admin-metric-card__label">Avg Per User</span>
                    <span className="admin-metric-card__sub">Today</span>
                  </div>
                </div>
                <div className="admin-metric-card">
                  <div className="admin-metric-card__icon admin-metric-card__icon--info" style={{ background: 'rgba(59,130,246,0.1)' }}>
                    <FaUsers />
                  </div>
                  <div className="admin-metric-card__content">
                    <span className="admin-metric-card__value">{stats.totalUsersWithAi}</span>
                    <span className="admin-metric-card__label">Users Ever</span>
                    <span className="admin-metric-card__sub">Have used AI</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Period Metrics */}
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <FaChartLine style={{ color: '#6366f1', fontSize: 13 }} />
                Last {statsPeriod} Days
              </h3>
              <div className="admin-analytics-metrics">
                <div className="admin-metric-card">
                  <div className="admin-metric-card__icon admin-metric-card__icon--primary" style={{ background: 'rgba(99,102,241,0.1)' }}>
                    <FaCommentDots />
                  </div>
                  <div className="admin-metric-card__content">
                    <span className="admin-metric-card__value">{stats.period.totalQueries.toLocaleString()}</span>
                    <span className="admin-metric-card__label">Total Queries</span>
                    <span className="admin-metric-card__sub">In period</span>
                  </div>
                </div>
                <div className="admin-metric-card">
                  <div className="admin-metric-card__icon admin-metric-card__icon--success" style={{ background: 'rgba(16,185,129,0.1)' }}>
                    <FaUsers />
                  </div>
                  <div className="admin-metric-card__content">
                    <span className="admin-metric-card__value">{stats.period.uniqueUsers}</span>
                    <span className="admin-metric-card__label">Unique Users</span>
                    <span className="admin-metric-card__sub">In period</span>
                  </div>
                </div>
                <div className="admin-metric-card">
                  <div className="admin-metric-card__icon admin-metric-card__icon--info" style={{ background: 'rgba(59,130,246,0.1)' }}>
                    <FaCrown />
                  </div>
                  <div className="admin-metric-card__content">
                    <span className="admin-metric-card__value">{stats.totalConversations.toLocaleString()}</span>
                    <span className="admin-metric-card__label">Conversations</span>
                    <span className="admin-metric-card__sub">All time</span>
                  </div>
                </div>
                <div className="admin-metric-card">
                  <div className="admin-metric-card__icon admin-metric-card__icon--warning" style={{ background: 'rgba(245,158,11,0.1)' }}>
                    <FaChartBar />
                  </div>
                  <div className="admin-metric-card__content">
                    <span className="admin-metric-card__value">
                      {stats.period.uniqueUsers > 0
                        ? Math.round(stats.period.totalQueries / stats.period.uniqueUsers)
                        : 0}
                    </span>
                    <span className="admin-metric-card__label">Avg Per User</span>
                    <span className="admin-metric-card__sub">In period</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Daily Trend Chart */}
            <div className="admin-charts-row" style={{ marginBottom: 24 }}>
              <div className="admin-chart-card admin-chart-card--large">
                <div className="admin-chart-card__header">
                  <h3 style={{ fontSize: 15, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FaChartLine style={{ color: '#6366f1' }} />
                    Daily Query Trend
                  </h3>
                </div>
                <div className="admin-chart-card__body">
                  {stats.dailyTrend.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={stats.dailyTrend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={(v) => v.slice(5)} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <defs>
                          <linearGradient id="queryGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <Area type="monotone" dataKey="queries" stroke="#6366f1" fill="url(#queryGrad)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
                      No query data available for this period.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Top Users */}
            <div className="admin-charts-row" style={{ marginBottom: 24 }}>
              <div className="admin-chart-card">
                <div className="admin-chart-card__header">
                  <h3 style={{ fontSize: 15, fontWeight: 600, color: '#374151' }}>
                    <FaCrown style={{ color: '#F59E0B', marginRight: 8 }} />
                    Top Users Today
                  </h3>
                </div>
                <div className="admin-chart-card__body" style={{ padding: 0 }}>
                  {stats.topUsersToday.length > 0 ? (
                    <table className="admin-table" style={{ margin: 0 }}>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>User</th>
                          <th style={{ textAlign: 'center' }}>Queries</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.topUsersToday.map((u, i) => (
                          <tr key={u._id}>
                            <td style={{ color: '#9ca3af', width: 40 }}>{i + 1}</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                {u.photo ? (
                                  <img src={u.photo} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                                ) : (
                                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#6b7280' }}>
                                    <FaUsers />
                                  </div>
                                )}
                                <div>
                                  <div style={{ fontSize: 13, fontWeight: 500 }}>{u.name}</div>
                                  <div style={{ fontSize: 11, color: '#9ca3af' }}>{u.email}</div>
                                </div>
                              </div>
                            </td>
                            <td style={{ textAlign: 'center', fontWeight: 600, color: '#6366f1' }}>{u.queriesToday}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
                      No AI queries today.
                    </div>
                  )}
                </div>
              </div>

              <div className="admin-chart-card">
                <div className="admin-chart-card__header">
                  <h3 style={{ fontSize: 15, fontWeight: 600, color: '#374151' }}>
                    <FaCrown style={{ color: '#6366f1', marginRight: 8 }} />
                    Top Users All Time
                  </h3>
                </div>
                <div className="admin-chart-card__body" style={{ padding: 0 }}>
                  {stats.topUsersAllTime.length > 0 ? (
                    <table className="admin-table" style={{ margin: 0 }}>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>User</th>
                          <th style={{ textAlign: 'center' }}>Conversations</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.topUsersAllTime.map((u, i) => (
                          <tr key={u._id?.toString() || i}>
                            <td style={{ color: '#9ca3af', width: 40 }}>{i + 1}</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                {u.photo ? (
                                  <img src={u.photo} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                                ) : (
                                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#6b7280' }}>
                                    <FaUsers />
                                  </div>
                                )}
                                <div>
                                  <div style={{ fontSize: 13, fontWeight: 500 }}>{u.name || 'Unknown'}</div>
                                  <div style={{ fontSize: 11, color: '#9ca3af' }}>{u.email || ''}</div>
                                </div>
                              </div>
                            </td>
                            <td style={{ textAlign: 'center', fontWeight: 600, color: '#6366f1' }}>{u.conversationCount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
                      No conversations yet.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default AiAssistantSettings;
